import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useSocket } from '../hooks/useSocket';

// ── Constantes do negócio ─────────────────────────────────────────
const DOCS_PADRAO = ['RG','CPF','Comprovante de residência','Foto 3x4','Histórico escolar'];

const SUBSTATUS_CONFIG = {
  mensagem_enviada:     { cor:'#0ea5e9', label:'Msg enviada',        icone:'📤' },
  nao_respondeu:        { cor:'#f59e0b', label:'Não respondeu',       icone:'⏳' },
  respondeu:            { cor:'#22c55e', label:'Respondeu',           icone:'💬' },
  interesse_confirmado: { cor:'#8b5cf6', label:'Interesse confirmado',icone:'✅' },
  pediu_ligar_depois:   { cor:'#f59e0b', label:'Ligar depois',        icone:'📞' },
  nao_quer_contato:     { cor:'#ef4444', label:'Não quer contato',    icone:'🚫' },
  agendou:              { cor:'#22c55e', label:'Agendou',             icone:'📅' },
  nao_quis_agendar:     { cor:'#f59e0b', label:'Não quis agendar',    icone:'❌' },
  remarcou:             { cor:'#8b5cf6', label:'Remarcou',            icone:'🔄' },
  compareceu:           { cor:'#22c55e', label:'Compareceu',          icone:'🏫' },
  nao_compareceu:       { cor:'#ef4444', label:'Não compareceu',      icone:'😔' },
  docs_solicitados:     { cor:'#0ea5e9', label:'Docs solicitados',    icone:'📋' },
  docs_parciais:        { cor:'#f59e0b', label:'Docs parciais',       icone:'📄' },
  docs_completos:       { cor:'#22c55e', label:'Docs completos',      icone:'📁' },
  matriculado:          { cor:'#22c55e', label:'Matriculado',         icone:'🎓' },
  desistiu:             { cor:'#ef4444', label:'Desistiu',            icone:'💔' },
};

// Substatus disponíveis por coluna (ordem certa do funil)
const SUBSTATUS_POR_COLUNA = {
  'Leads Novos':          ['mensagem_enviada','nao_respondeu','respondeu'],
  'Em Contato':           ['respondeu','pediu_ligar_depois','nao_quer_contato','interesse_confirmado'],
  'Interesse Confirmado': ['interesse_confirmado','pediu_ligar_depois','agendou','docs_solicitados'],
  'Agendamento':          ['agendou','remarcou','nao_quis_agendar','compareceu','nao_compareceu'],
  'Docs / Matrícula':     ['docs_solicitados','docs_parciais','docs_completos'],
  'Matriculado':          ['matriculado'],
  'Não Convertido':       ['nao_quer_contato','nao_compareceu','desistiu'],
};

const ORIGENS = { campanha:'📢 Campanha', ads:'📱 Meta Ads', indicacao:'👥 Indicação', site:'🌐 Site', outro:'📌 Outro' };
const PRIORIDADES = { baixa:'Baixa', media:'Média', alta:'Alta', urgente:'🔥 Urgente' };
const PRIOR_COR   = { baixa:'#64748b', media:'#0ea5e9', alta:'#f59e0b', urgente:'#ef4444' };

const fmtBRL  = v => v ? new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(v) : null;
const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : null;
const fmtHora = d => d ? new Date(d).toLocaleString('pt-BR') : null;
const fmtDT   = d => d ? new Date(d).toLocaleString('pt-BR',{dateStyle:'short',timeStyle:'short'}) : null;

const prazoSt = prazo => {
  if (!prazo) return null;
  const diff = new Date(prazo) - new Date();
  if (diff < 0)            return { cor:'#ef4444', txt:'Vencido' };
  if (diff < 86400000)     return { cor:'#ef4444', txt:'Hoje' };
  if (diff < 3*86400000)   return { cor:'#f59e0b', txt:fmtData(prazo) };
  return { cor:'#64748b', txt:fmtData(prazo) };
};

// ── Tokens visuais ────────────────────────────────────────────────
const C = {
  bg:'#0f172a', bg2:'#1e293b', bg3:'#0d1525',
  border:'#334155', text:'#f1f5f9', sub:'#94a3b8', muted:'#475569',
};

const s = {
  page:   { display:'flex', flexDirection:'column', height:'calc(100vh - 80px)' },
  tbar:   { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, gap:12 },
  tit:    { fontSize:20, fontWeight:700, color:C.text },
  sub:    { fontSize:12, color:C.muted, marginTop:2 },
  board:  { display:'flex', gap:10, overflowX:'auto', flex:1, paddingBottom:12, alignItems:'flex-start' },
  col:    (over) => ({
            width:250, flexShrink:0, background:over?'rgba(14,165,233,.04)':C.bg2,
            border:`1px solid ${over?'#0ea5e9':C.border}`,
            borderRadius:12, display:'flex', flexDirection:'column',
            maxHeight:'100%', transition:'all .15s'
          }),
  cHdr:   (cor) => ({
            padding:'10px 12px', borderBottom:`1px solid ${C.border}`,
            borderTop:`3px solid ${cor}`, borderRadius:'11px 11px 0 0',
            display:'flex', flexDirection:'column', gap:2
          }),
  cNm:    { fontSize:13, fontWeight:700, color:C.text },
  cSub:   { fontSize:11, color:C.muted },
  cBdy:   { flex:1, overflowY:'auto', padding:'8px', display:'flex', flexDirection:'column', gap:8 },
  addBtn: { width:'100%', background:'transparent', border:`1px dashed ${C.border}`,
            color:C.muted, borderRadius:8, padding:'7px', cursor:'pointer', fontSize:12 },
  card:   { background:C.bg3, border:`1px solid ${C.border}`, borderRadius:10,
            padding:'10px 11px', cursor:'pointer', userSelect:'none',
            transition:'border-color .1s', ':hover':{ borderColor:'#0ea5e9' } },
  cNome:  { fontSize:13, fontWeight:600, color:C.text, marginBottom:3,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  cTit:   { fontSize:11, color:C.sub, marginBottom:6, lineHeight:1.4,
            display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' },
  badge:  (c) => ({ background:c+'22', color:c, padding:'2px 7px', borderRadius:10, fontSize:10, fontWeight:700 }),
  pill:   (c) => ({ background:c+'15', color:c, padding:'1px 6px', borderRadius:6, fontSize:9, fontWeight:700 }),
  valor:  { fontSize:11, fontWeight:700, color:'#22c55e' },
  phone:  { fontSize:10, color:C.muted },
  cFtr:   { display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:6, flexWrap:'wrap', gap:3 },
  cVisita:{ background:'rgba(236,72,153,.1)', color:'#ec4899', borderRadius:6,
            padding:'2px 6px', fontSize:9, fontWeight:700, marginTop:4 },
  cDocs:  { display:'flex', gap:3, marginTop:4, flexWrap:'wrap' },
  docTag: (ok) => ({ background:ok?'rgba(34,197,94,.1)':'rgba(100,116,139,.1)',
                     color:ok?'#22c55e':'#64748b', fontSize:9, padding:'1px 5px',
                     borderRadius:4, fontWeight:600 }),
  // Modal
  overlay:{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
  modal:  { background:C.bg2, border:`1px solid ${C.border}`, borderRadius:16,
            width:'100%', maxWidth:600, maxHeight:'88vh', overflowY:'auto', padding:24 },
  mTit:   { fontSize:17, fontWeight:700, color:C.text, marginBottom:16 },
  label:  { fontSize:12, color:C.sub, marginBottom:5, display:'block' },
  input:  { width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
            padding:'8px 11px', color:C.text, fontSize:13, outline:'none', marginBottom:11, fontFamily:'inherit' },
  tarea:  { width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
            padding:'8px 11px', color:C.text, fontSize:13, outline:'none', marginBottom:11,
            resize:'vertical', minHeight:64, fontFamily:'inherit' },
  sel:    { width:'100%', background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
            padding:'8px 11px', color:C.text, fontSize:13, marginBottom:11 },
  grid2:  { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 },
  btn:    { background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
            padding:'8px 18px', cursor:'pointer', fontSize:13, fontWeight:600 },
  btnSm:  { background:C.bg2, color:C.sub, border:`1px solid ${C.border}`,
            borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:12 },
  btnRed: { background:'transparent', color:'#ef4444', border:`1px solid rgba(239,68,68,.3)`,
            borderRadius:8, padding:'5px 12px', cursor:'pointer', fontSize:12 },
  hr:     { border:'none', borderTop:`1px solid ${C.border}`, margin:'14px 0' },
  secTit: { fontSize:12, fontWeight:600, color:C.sub, textTransform:'uppercase',
            letterSpacing:'.5px', marginBottom:10 },
  nota:   { background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
            padding:'9px 11px', marginBottom:7, fontSize:12, color:C.text },
  notaMt: { fontSize:10, color:C.muted, marginTop:3 },
  tRef:   { display:'flex', alignItems:'center', gap:8, padding:'5px 0',
            borderBottom:`1px solid #1e293b`, fontSize:12 },
  check:  (ok) => ({ width:15, height:15, borderRadius:3, border:`1px solid ${ok?'#22c55e':C.border}`,
                     background:ok?'#22c55e':'transparent', cursor:'pointer', flexShrink:0,
                     display:'flex', alignItems:'center', justifyContent:'center' }),
  histRow:{ padding:'5px 0', borderBottom:`1px solid #1e293b`, fontSize:11, color:C.muted },
  tabs:   { display:'flex', gap:0, borderBottom:`1px solid ${C.border}`, marginBottom:14 },
  tab:    (a) => ({ padding:'6px 14px', background:'none', border:'none', cursor:'pointer',
                    fontSize:12, color:a?'#0ea5e9':C.sub,
                    borderBottom:`2px solid ${a?'#0ea5e9':'transparent'}` }),
  filters:{ display:'flex', gap:8, flexWrap:'wrap' },
  inp2:   { background:C.bg2, border:`1px solid ${C.border}`, color:C.text,
            borderRadius:8, padding:'6px 10px', fontSize:12, outline:'none' },
  sel2:   { background:C.bg2, border:`1px solid ${C.border}`, color:C.text,
            borderRadius:8, padding:'6px 10px', fontSize:12 },
  viewBtn:(a) => ({ background:a?'#0ea5e9':C.bg2, color:a?'#fff':C.sub, border:`1px solid ${a?'#0ea5e9':C.border}`, borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:700 }),
  agendaGrid:{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12, overflowY:'auto', paddingBottom:16 },
  agendaCard:{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:10, padding:14 },
  agendaTime:{ fontSize:20, fontWeight:800, color:'#38bdf8' },
  agendaName:{ fontSize:14, fontWeight:700, color:C.text, marginTop:8 },
  agendaMeta:{ fontSize:12, color:C.sub, marginTop:4, lineHeight:1.45 },
  ok:     { background:'rgba(34,197,94,.1)', color:'#4ade80', borderRadius:8, padding:'9px 12px', fontSize:12, marginBottom:10 },
  err:    { background:'rgba(239,68,68,.1)', color:'#f87171', borderRadius:8, padding:'9px 12px', fontSize:12, marginBottom:10 },
};

// ── Badge de substatus ────────────────────────────────────────────
function SubstatusBadge({ substatus }) {
  if (!substatus) return null;
  const cfg = SUBSTATUS_CONFIG[substatus] || { cor:'#64748b', label:substatus, icone:'·' };
  return (
    <span style={s.badge(cfg.cor)}>
      {cfg.icone} {cfg.label}
    </span>
  );
}

// ── Card do kanban ────────────────────────────────────────────────
function KCard({ card, onOpen, onDragStart, onDragEnd }) {
  const ps = prazoSt(card.prazo);
  const docsOk = card.docs_recebidos || [];
  const docsPend = card.docs_pendentes || [];

  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('cardId', card.id); onDragStart(card); }}
      onDragEnd={onDragEnd}
      style={s.card}
      onClick={() => onOpen(card)}
    >
      <div style={s.cNome}>{card.nome || card.phone}</div>
      {card.curso_interesse && <div style={s.cTit}>📚 {card.curso_interesse}</div>}
      {card.titulo && !card.curso_interesse && <div style={s.cTit}>{card.titulo}</div>}

      {card.substatus && <div style={{ marginBottom:5 }}><SubstatusBadge substatus={card.substatus} /></div>}

      {card.visita_data && (
        <div style={s.cVisita}>
          📅 {fmtDT(card.visita_data)}{card.visita_confirmada ? ' ✓' : ''}
        </div>
      )}

      {(docsOk.length > 0 || docsPend.length > 0) && (
        <div style={s.cDocs}>
          {DOCS_PADRAO.map(d => (
            <span key={d} style={s.docTag(docsOk.includes(d))} title={d}>
              {docsOk.includes(d) ? '✓' : '·'} {d.split(' ')[0]}
            </span>
          ))}
        </div>
      )}

      <div style={s.cFtr}>
        <span style={s.phone}>{card.phone}</span>
        <div style={{ display:'flex', gap:4, alignItems:'center', flexWrap:'wrap' }}>
          {card.valor && <span style={s.valor}>{fmtBRL(card.valor)}</span>}
          {ps && <span style={s.badge(ps.cor)}>{ps.txt}</span>}
          {card.prioridade !== 'media' &&
            <span style={s.badge(PRIOR_COR[card.prioridade])}>{PRIORIDADES[card.prioridade]}</span>}
          {card.origem && card.origem !== 'campanha' &&
            <span style={s.pill('#64748b')}>{ORIGENS[card.origem]}</span>}
        </div>
      </div>

      {(card.total_notas > 0 || card.total_tarefas > 0) && (
        <div style={{ fontSize:9, color:C.muted, marginTop:5 }}>
          {card.total_notas > 0 && `💬 ${card.total_notas}`}
          {card.total_tarefas > 0 && ` · ✓ ${card.tarefas_concluidas}/${card.total_tarefas}`}
        </div>
      )}
    </div>
  );
}

// ── Modal de detalhes ─────────────────────────────────────────────
function ModalDetalhes({ card, colunas, atendentes, onClose, onSalvo }) {
  const [dados, setDados]   = useState(null);
  const [aba, setAba]       = useState('info');
  const [edit, setEdit]     = useState(false);
  const [form, setForm]     = useState({});
  const [novaNota, setNN]   = useState('');
  const [novaTar, setNT]    = useState('');
  const [saving, setSav]    = useState(false);
  const [msg, setMsg]       = useState(null);
  const [colDest, setCD]    = useState('');
  const [novoSub, setNSub]  = useState('');

  const carregar = () =>
    api.get(`/kanban/cards/${card.id}`).then(r => {
      setDados(r.data);
      setForm({
        nome: r.data.nome||'', titulo: r.data.titulo||'',
        curso_interesse: r.data.curso_interesse||'',
        valor: r.data.valor||'', prioridade: r.data.prioridade||'media',
        origem: r.data.origem||'campanha',
        prazo: r.data.prazo ? r.data.prazo.substring(0,10) : '',
        descricao: r.data.descricao||'', atendente_id: r.data.atendente_id||'',
        etiquetas: r.data.etiquetas||[], substatus: r.data.substatus||'',
        visita_data: r.data.visita_data ? new Date(r.data.visita_data).toISOString().substring(0,16) : '',
        visita_confirmada: r.data.visita_confirmada||false,
        docs_recebidos: r.data.docs_recebidos||[],
        docs_pendentes: r.data.docs_pendentes||DOCS_PADRAO,
      });
    });

  useEffect(() => { carregar(); }, [card.id]);

  const salvar = async () => {
    setSav(true); setMsg(null);
    try {
      await api.patch(`/kanban/cards/${card.id}`, {
        ...form, valor: form.valor ? parseFloat(form.valor) : null,
        visita_data: form.visita_data || null
      });
      setMsg({ ok:true, txt:'Salvo!' });
      setEdit(false); carregar(); onSalvo();
    } catch { setMsg({ ok:false, txt:'Erro ao salvar' }); }
    finally { setSav(false); }
  };

  const mover = async () => {
    if (!colDest) return;
    await api.patch(`/kanban/cards/${card.id}/mover`, { coluna_destino_id: colDest });
    carregar(); onSalvo();
  };

  const atualizarSub = async () => {
    if (!novoSub) return;
    await api.patch(`/kanban/cards/${card.id}`, { substatus: novoSub });
    carregar(); onSalvo();
  };

  const addNota = async () => {
    if (!novaNota.trim()) return;
    await api.post(`/kanban/cards/${card.id}/notas`, { texto: novaNota });
    setNN(''); carregar();
  };

  const addTar = async () => {
    if (!novaTar.trim()) return;
    await api.post(`/kanban/cards/${card.id}/tarefas`, { texto: novaTar });
    setNT(''); carregar();
  };

  const toggleTar = async (t) => {
    await api.patch(`/kanban/tarefas/${t.id}`, { concluida: !t.concluida });
    carregar();
  };

  const toggleDoc = (doc) => {
    const cur = form.docs_recebidos || [];
    setForm(f => ({
      ...f,
      docs_recebidos: cur.includes(doc) ? cur.filter(d=>d!==doc) : [...cur, doc]
    }));
  };

  if (!dados) return (
    <div style={s.overlay}>
      <div style={{ color:C.sub }}>Carregando...</div>
    </div>
  );

  const substatusDisp = (colunas.find(c=>c.id===dados.coluna_id)?.nome
    ? SUBSTATUS_POR_COLUNA[colunas.find(c=>c.id===dados.coluna_id)?.nome] || []
    : []);

  const tarefasPct = dados.tarefas?.length
    ? (dados.tarefas.filter(t=>t.concluida).length / dados.tarefas.length) * 100 : 0;

  return (
    <div style={s.overlay} onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={s.modal}>
        {/* Cabeçalho */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:4 }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:dados.coluna_cor, display:'inline-block' }} />
              <span style={{ fontSize:11, color:C.muted }}>{dados.coluna_nome}</span>
              {dados.substatus && <SubstatusBadge substatus={dados.substatus} />}
            </div>
            <div style={{ fontSize:17, fontWeight:700, color:C.text }}>{dados.nome || dados.phone}</div>
            <div style={{ fontSize:11, color:C.muted }}>{dados.phone}</div>
          </div>
          <button style={{ background:'none', border:'none', color:C.sub, cursor:'pointer', fontSize:18 }}
            onClick={onClose}>✕</button>
        </div>

        {msg && <div style={msg.ok?s.ok:s.err}>{msg.txt}</div>}

        <div style={s.tabs}>
          {[['info','📋 Info'],['agendamento','📅 Visita'],['docs','📄 Docs'],
            ['notas',`💬 Notas${dados.notas?.length?` (${dados.notas.length})`:''}` ],
            ['tarefas',`✓ Tarefas${dados.tarefas?.length?` (${dados.tarefas.length})`:''}` ],
            ['historico','🕓 Histórico']].map(([k,l]) => (
            <button key={k} style={s.tab(aba===k)} onClick={()=>setAba(k)}>{l}</button>
          ))}
        </div>

        {/* ABA INFO */}
        {aba === 'info' && (
          edit ? (
            <div>
              <label style={s.label}>Nome do lead</label>
              <input style={s.input} value={form.nome}
                onChange={e=>setForm(f=>({...f,nome:e.target.value}))} />
              <label style={s.label}>Curso de interesse</label>
              <input style={s.input} value={form.curso_interesse}
                placeholder="Gestão & Administração Digital..."
                onChange={e=>setForm(f=>({...f,curso_interesse:e.target.value}))} />
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>Valor da matrícula (R$)</label>
                  <input style={s.input} type="number" value={form.valor}
                    onChange={e=>setForm(f=>({...f,valor:e.target.value}))} />
                </div>
                <div>
                  <label style={s.label}>Prazo</label>
                  <input style={s.input} type="date" value={form.prazo}
                    onChange={e=>setForm(f=>({...f,prazo:e.target.value}))} />
                </div>
              </div>
              <div style={s.grid2}>
                <div>
                  <label style={s.label}>Origem</label>
                  <select style={s.sel} value={form.origem}
                    onChange={e=>setForm(f=>({...f,origem:e.target.value}))}>
                    {Object.entries(ORIGENS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Prioridade</label>
                  <select style={s.sel} value={form.prioridade}
                    onChange={e=>setForm(f=>({...f,prioridade:e.target.value}))}>
                    {Object.entries(PRIORIDADES).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <label style={s.label}>Atendente</label>
              <select style={s.sel} value={form.atendente_id}
                onChange={e=>setForm(f=>({...f,atendente_id:e.target.value}))}>
                <option value="">Sem atendente</option>
                {atendentes.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <label style={s.label}>Observações</label>
              <textarea style={s.tarea} value={form.descricao}
                onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} />
              <div style={{ display:'flex', gap:8 }}>
                <button style={s.btn} onClick={salvar} disabled={saving}>
                  {saving?'Salvando...':'💾 Salvar'}
                </button>
                <button style={s.btnSm} onClick={()=>setEdit(false)}>Cancelar</button>
              </div>
            </div>
          ) : (
            <div>
              {/* Substatus rápido */}
              <div style={{ marginBottom:14 }}>
                <div style={s.secTit}>Atualizar status</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {substatusDisp.map(sub => {
                    const cfg = SUBSTATUS_CONFIG[sub] || { cor:'#64748b', label:sub, icone:'·' };
                    return (
                      <button key={sub}
                        style={{ ...s.badge(cfg.cor), border:`1px solid ${cfg.cor}30`,
                          cursor:'pointer', padding:'4px 10px', fontSize:11,
                          opacity: dados.substatus===sub ? 1 : 0.5 }}
                        onClick={async () => {
                          await api.patch(`/kanban/cards/${card.id}`, { substatus: sub });
                          carregar(); onSalvo();
                        }}>
                        {cfg.icone} {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={s.hr} />

              {/* Informações */}
              <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:12 }}>
                {dados.curso_interesse &&
                  <div><span style={{color:C.muted,fontSize:12}}>Curso: </span>
                    <span style={{color:C.text,fontSize:13}}>📚 {dados.curso_interesse}</span></div>}
                {dados.valor &&
                  <div><span style={{color:C.muted,fontSize:12}}>Valor: </span>
                    <span style={{color:'#22c55e',fontWeight:700}}>{fmtBRL(dados.valor)}</span></div>}
                {dados.atendente_nome &&
                  <div><span style={{color:C.muted,fontSize:12}}>Atendente: </span>
                    <span style={{color:C.text,fontSize:13}}>{dados.atendente_nome}</span></div>}
                {dados.origem &&
                  <div><span style={{color:C.muted,fontSize:12}}>Origem: </span>
                    <span style={{color:C.text,fontSize:13}}>{ORIGENS[dados.origem]}</span></div>}
              </div>

              {dados.descricao && (
                <div style={{ fontSize:12, color:C.sub, background:C.bg,
                  padding:'9px 11px', borderRadius:8, marginBottom:12 }}>
                  {dados.descricao}
                </div>
              )}

              {/* Mover coluna */}
              <div style={s.hr} />
              <div style={s.secTit}>Mover para coluna</div>
              <div style={{ display:'flex', gap:8 }}>
                <select style={{ ...s.sel, flex:1, marginBottom:0 }}
                  value={colDest} onChange={e=>setCD(e.target.value)}>
                  <option value="">Selecionar...</option>
                  {colunas.filter(c=>c.id!==dados.coluna_id).map(c=>(
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
                <button style={s.btn} onClick={mover} disabled={!colDest}>Mover</button>
              </div>

              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <button style={s.btnSm} onClick={()=>setEdit(true)}>✏️ Editar</button>
                <button style={s.btnRed} onClick={async()=>{
                  if(window.confirm('Arquivar este lead?')){
                    await api.patch(`/kanban/cards/${card.id}/arquivar`);
                    onSalvo(); onClose();
                  }
                }}>🗄 Arquivar</button>
              </div>
            </div>
          )
        )}

        {/* ABA AGENDAMENTO */}
        {aba === 'agendamento' && (
          <div>
            <div style={s.secTit}>Visita presencial na escola</div>
            {dados.visita_data && (
              <div style={{ background:'rgba(236,72,153,.08)', border:'1px solid rgba(236,72,153,.2)',
                borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#ec4899' }}>
                  📅 {fmtDT(dados.visita_data)}
                  {dados.visita_confirmada && <span style={{ marginLeft:8, color:'#22c55e' }}>✓ Confirmada</span>}
                </div>
                <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>
                  📍 {dados.visita_local || 'Grupo D1 — Pederneiras/SP'}
                </div>
              </div>
            )}
            <label style={s.label}>Data e hora da visita</label>
            <input style={s.input} type="datetime-local" value={form.visita_data}
              onChange={e=>setForm(f=>({...f,visita_data:e.target.value}))} />
            <label style={s.label}>Local</label>
            <input style={s.input} value={form.visita_local || 'Grupo D1 — Pederneiras/SP'}
              onChange={e=>setForm(f=>({...f,visita_local:e.target.value}))} />
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
              <input type="checkbox" id="vis-conf" checked={form.visita_confirmada}
                onChange={e=>setForm(f=>({...f,visita_confirmada:e.target.checked}))} />
              <label htmlFor="vis-conf" style={{ fontSize:13, color:C.sub, cursor:'pointer' }}>
                Visita confirmada pelo lead
              </label>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button style={s.btn} onClick={async()=>{
                await api.patch(`/kanban/cards/${card.id}`, {
                  visita_data: form.visita_data||null,
                  visita_confirmada: form.visita_confirmada,
                  substatus: form.visita_data ? 'agendou' : dados.substatus
                });
                setMsg({ok:true,txt:'Agendamento salvo!'}); carregar(); onSalvo();
              }}>💾 Salvar agendamento</button>
              {dados.visita_data && (
                <button style={s.btnRed} onClick={async()=>{
                  await api.patch(`/kanban/cards/${card.id}`,
                    { visita_data:null, substatus:'nao_compareceu' });
                  carregar(); onSalvo();
                }}>✕ Desmarcar</button>
              )}
            </div>
          </div>
        )}

        {/* ABA DOCS */}
        {aba === 'docs' && (
          <div>
            <div style={s.secTit}>Documentos para matrícula</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>
              Marque os documentos já recebidos pelo WhatsApp
            </div>
            {DOCS_PADRAO.map(doc => {
              const recebido = (form.docs_recebidos||[]).includes(doc);
              return (
                <div key={doc} style={{ display:'flex', alignItems:'center', gap:10,
                  padding:'8px 0', borderBottom:`1px solid #1e293b` }}>
                  <div style={{ ...s.check(recebido), width:18, height:18 }}
                    onClick={() => toggleDoc(doc)}>
                    {recebido && <span style={{ color:'#fff', fontSize:11 }}>✓</span>}
                  </div>
                  <span style={{ fontSize:13, color:recebido?'#22c55e':C.sub,
                    textDecoration:recebido?'none':'none', flex:1 }}>{doc}</span>
                  {recebido && <span style={{ fontSize:10, color:'#22c55e' }}>✓ Recebido</span>}
                </div>
              );
            })}
            <div style={{ marginTop:14, display:'flex', gap:8 }}>
              <button style={s.btn} onClick={async()=>{
                const allDocs = DOCS_PADRAO;
                const recebidos = form.docs_recebidos||[];
                const pendentes = allDocs.filter(d=>!recebidos.includes(d));
                const sub = pendentes.length===0?'docs_completos':recebidos.length>0?'docs_parciais':'docs_solicitados';
                await api.patch(`/kanban/cards/${card.id}`,
                  { docs_recebidos:recebidos, docs_pendentes:pendentes, substatus:sub });
                setMsg({ok:true,txt:'Documentos atualizados!'}); carregar(); onSalvo();
              }}>💾 Salvar documentos</button>
            </div>
            {(form.docs_recebidos||[]).length === DOCS_PADRAO.length && (
              <div style={{ ...s.ok, marginTop:12 }}>
                ✅ Todos os documentos recebidos! Pronto para matrícula.
              </div>
            )}
          </div>
        )}

        {/* ABA NOTAS */}
        {aba === 'notas' && (
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
              <textarea style={{ ...s.tarea, flex:1, minHeight:54, marginBottom:0 }}
                value={novaNota} placeholder="Adicionar nota sobre este lead..."
                onChange={e=>setNN(e.target.value)} />
              <button style={{ ...s.btn, alignSelf:'flex-end' }} onClick={addNota}>+</button>
            </div>
            {(dados.notas||[]).map(n=>(
              <div key={n.id} style={s.nota}>
                <div>{n.texto}</div>
                <div style={s.notaMt}>{n.autor_nome||'Admin'} · {fmtHora(n.created_at)}</div>
              </div>
            ))}
            {!dados.notas?.length && <div style={{ color:C.muted, fontSize:12 }}>Sem notas.</div>}
          </div>
        )}

        {/* ABA TAREFAS */}
        {aba === 'tarefas' && (
          <div>
            {dados.tarefas?.length > 0 && (
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:3 }}>
                  {dados.tarefas.filter(t=>t.concluida).length}/{dados.tarefas.length} concluídas
                </div>
                <div style={{ height:4, background:'#1e293b', borderRadius:2 }}>
                  <div style={{ height:'100%', width:`${tarefasPct}%`,
                    background:'#22c55e', borderRadius:2, transition:'width .3s' }} />
                </div>
              </div>
            )}
            {(dados.tarefas||[]).map(t=>(
              <div key={t.id} style={s.tRef}>
                <div style={s.check(t.concluida)} onClick={()=>toggleTar(t)}>
                  {t.concluida && <span style={{ color:'#fff', fontSize:9 }}>✓</span>}
                </div>
                <span style={{ flex:1, color:t.concluida?C.muted:C.text,
                  textDecoration:t.concluida?'line-through':'none' }}>{t.texto}</span>
                <button style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:11 }}
                  onClick={async()=>{ await api.delete(`/kanban/tarefas/${t.id}`); carregar(); }}>✕</button>
              </div>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:10 }}>
              <input style={{ ...s.input, flex:1, marginBottom:0 }} value={novaTar}
                placeholder="Nova tarefa..." onChange={e=>setNT(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addTar()} />
              <button style={s.btn} onClick={addTar}>+</button>
            </div>
          </div>
        )}

        {/* ABA HISTÓRICO */}
        {aba === 'historico' && (
          <div>
            {(dados.historico||[]).map(h=>(
              <div key={h.id} style={s.histRow}>
                <span style={{ color:h.tipo==='movido'?'#0ea5e9':h.tipo==='agendou'?'#ec4899':
                  h.tipo==='matriculou'?'#22c55e':h.tipo==='criado'?'#8b5cf6':C.muted }}>
                  {h.tipo==='movido'?'↔':h.tipo==='agendou'?'📅':h.tipo==='criado'?'✦':'·'} {h.tipo}
                </span>
                {h.coluna_origem_nome && h.coluna_destino_nome &&
                  <span style={{ color:C.muted, marginLeft:6 }}>
                    {h.coluna_origem_nome} → {h.coluna_destino_nome}
                  </span>}
                {h.substatus_depois &&
                  <span style={{ marginLeft:6 }}><SubstatusBadge substatus={h.substatus_depois} /></span>}
                {h.descricao && <span style={{ marginLeft:6, color:C.muted }}>{h.descricao}</span>}
                <div style={{ color:C.muted, fontSize:10, marginTop:1 }}>{fmtHora(h.created_at)}</div>
              </div>
            ))}
            {!dados.historico?.length && <div style={{ color:C.muted, fontSize:12 }}>Sem histórico.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal novo card ───────────────────────────────────────────────
function ModalNovo({ colunaId, atendentes, onSalvo, onClose }) {
  const [form, setForm] = useState({
    phone:'', nome:'', curso_interesse:'Gestão & Administração Digital',
    valor:'', prioridade:'media', atendente_id:'', origem:'campanha',
    substatus:'mensagem_enviada'
  });
  const [saving, setSav] = useState(false);

  const salvar = async () => {
    if (!form.phone.trim()) return;
    setSav(true);
    try {
      await api.post('/kanban/cards', {
        ...form, coluna_id: colunaId,
        valor: form.valor ? parseFloat(form.valor) : null,
        docs_pendentes: DOCS_PADRAO
      });
      onSalvo(); onClose();
    } finally { setSav(false); }
  };

  return (
    <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ ...s.modal, maxWidth:440 }}>
        <div style={s.mTit}>Novo lead</div>
        <label style={s.label}>Telefone *</label>
        <input style={s.input} value={form.phone} placeholder="14999887766"
          onChange={e=>setForm(f=>({...f,phone:e.target.value.replace(/\D/g,'')}))} />
        <label style={s.label}>Nome</label>
        <input style={s.input} value={form.nome}
          onChange={e=>setForm(f=>({...f,nome:e.target.value}))} />
        <label style={s.label}>Curso de interesse</label>
        <input style={s.input} value={form.curso_interesse}
          onChange={e=>setForm(f=>({...f,curso_interesse:e.target.value}))} />
        <div style={s.grid2}>
          <div>
            <label style={s.label}>Origem</label>
            <select style={s.sel} value={form.origem}
              onChange={e=>setForm(f=>({...f,origem:e.target.value}))}>
              {Object.entries(ORIGENS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={s.label}>Prioridade</label>
            <select style={s.sel} value={form.prioridade}
              onChange={e=>setForm(f=>({...f,prioridade:e.target.value}))}>
              {Object.entries(PRIORIDADES).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        <label style={s.label}>Atendente</label>
        <select style={s.sel} value={form.atendente_id}
          onChange={e=>setForm(f=>({...f,atendente_id:e.target.value}))}>
          <option value="">Sem atendente</option>
          {atendentes.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div style={{ display:'flex', gap:8 }}>
          <button style={s.btn} onClick={salvar} disabled={saving||!form.phone}>
            {saving?'Criando...':'Criar lead'}
          </button>
          <button style={s.btnSm} onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Coluna ────────────────────────────────────────────────────────
function Coluna({ col, cards, atendentes, onOpen, onDragStart, onDragEnd, onDrop, onNovo }) {
  const [over, setOver] = useState(false);
  return (
    <div style={s.col(over)}
      onDragOver={e=>{e.preventDefault();setOver(true);}}
      onDragLeave={()=>setOver(false)}
      onDrop={e=>{e.preventDefault();setOver(false);onDrop(e,col.id);}}>
      <div style={s.cHdr(col.cor)}>
        <div style={s.cNm}>{col.nome}</div>
        <div style={s.cSub}>
          {cards.length} lead{cards.length!==1?'s':''}
          {col.valor_total > 0 && ` · ${fmtBRL(col.valor_total)}`}
        </div>
      </div>
      <div style={s.cBdy}>
        {cards.map(card => (
          <KCard key={card.id} card={card} onOpen={onOpen}
            onDragStart={onDragStart} onDragEnd={onDragEnd} />
        ))}
        <button style={s.addBtn} onClick={()=>onNovo(col.id)}>+ Novo lead</button>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────
function AgendaView({ agendamentos, onOpenCard }) {
  const grupos = agendamentos.reduce((acc, ag) => {
    const key = ag.data_agendada?.substring(0, 10) || 'sem-data';
    acc[key] = acc[key] || [];
    acc[key].push(ag);
    return acc;
  }, {});

  const datas = Object.keys(grupos).sort();
  if (!datas.length) {
    return (
      <div style={{ ...s.agendaCard, color:C.muted, textAlign:'center', padding:32 }}>
        Nenhum agendamento encontrado. Clique em "Organizar agenda" para o Gestor IA analisar as conversas.
      </div>
    );
  }

  return (
    <div style={{ overflowY:'auto', flex:1 }}>
      {datas.map(data => (
        <div key={data} style={{ marginBottom:18 }}>
          <div style={{ ...s.secTit, color:'#38bdf8' }}>
            {new Date(data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'2-digit' })}
          </div>
          <div style={s.agendaGrid}>
            {grupos[data].map(ag => (
              <div key={ag.id} style={s.agendaCard}>
                <div style={{ display:'flex', justifyContent:'space-between', gap:10 }}>
                  <div style={s.agendaTime}>{String(ag.hora_agendada || '').substring(0,5)}</div>
                  <span style={s.badge(ag.status === 'agendado' ? '#22c55e' : '#f59e0b')}>{ag.status}</span>
                </div>
                <div style={s.agendaName}>{ag.nome || ag.phone}</div>
                <div style={s.agendaMeta}>
                  Tel: {ag.phone}<br />
                  Quem agendou: {ag.atendente_nome || ag.atendente_atual || '-'}<br />
                  Local: {ag.local || 'Grupo D1'}
                </div>
                {ag.trecho && (
                  <div style={{ ...s.agendaMeta, background:C.bg, padding:8, borderRadius:8, marginTop:8 }}>
                    {ag.trecho.slice(-180)}
                  </div>
                )}
                {ag.card_id && (
                  <button style={{ ...s.btnSm, marginTop:10 }} onClick={() => onOpenCard(ag.card_id)}>
                    Abrir card
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Kanban() {
  const { socket }                  = useSocket();
  const [colunas, setColunas]       = useState([]);
  const [cards, setCards]           = useState([]);
  const [atendentes, setAt]         = useState([]);
  const [cardAberto, setAberto]     = useState(null);
  const [novoCardCol, setNovoCol]   = useState(null);
  const [filtros, setFiltros]       = useState({ busca:'', atendente_id:'', substatus:'' });
  const [view, setView]             = useState('kanban');
  const [agendamentos, setAg]       = useState([]);
  const [organizando, setOrg]       = useState(false);

  const carregar = useCallback(async () => {
    const hoje = new Date();
    const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 7).toISOString().slice(0,10);
    const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 30).toISOString().slice(0,10);
    const [c, k, a, ag] = await Promise.all([
      api.get('/kanban/colunas'),
      api.get('/kanban/cards'),
      api.get('/atendentes'),
      api.get(`/gestor/agendamentos?inicio=${inicio}&fim=${fim}`)
    ]);
    setColunas(c.data); setCards(k.data); setAt(a.data);
    setAg(ag.data || []);
  }, []);

  useEffect(()=>{ carregar(); },[carregar]);

  useEffect(()=>{
    if(!socket) return;
    const h = ()=>carregar();
    socket.on('kanban_card_criado',h);
    socket.on('kanban_card_movido',h);
    socket.on('kanban_card_atualizado',h);
    return ()=>{
      socket.off('kanban_card_criado',h);
      socket.off('kanban_card_movido',h);
      socket.off('kanban_card_atualizado',h);
    };
  },[socket,carregar]);

  const onDrop = async (e, colDest) => {
    const id = e.dataTransfer.getData('cardId');
    const c  = cards.find(x=>x.id===id);
    if (!c || c.coluna_id===colDest) return;
    setCards(prev=>prev.map(x=>x.id===id?{...x,coluna_id:colDest}:x));
    await api.patch(`/kanban/cards/${id}/mover`, { coluna_destino_id: colDest });
    carregar();
  };

  const organizarAgenda = async () => {
    setOrg(true);
    try {
      await api.post('/gestor/agendamentos/processar', { dias: 2 });
      await carregar();
      setView('agenda');
    } finally {
      setOrg(false);
    }
  };

  const abrirCardAgenda = (cardId) => {
    const card = cards.find(c=>c.id===cardId);
    if (card) setAberto(card);
  };

  const filtrados = cards.filter(c => {
    if (c.arquivado) return false;
    if (filtros.atendente_id && c.atendente_id !== filtros.atendente_id) return false;
    if (filtros.substatus     && c.substatus !== filtros.substatus)         return false;
    if (filtros.busca) {
      const b = filtros.busca.toLowerCase();
      return (c.nome||'').toLowerCase().includes(b) || c.phone.includes(b)
        || (c.curso_interesse||'').toLowerCase().includes(b);
    }
    return true;
  });

  const cardsDaCol = id => filtrados.filter(c=>c.coluna_id===id);
  const totalPipeline = cards.filter(c=>!c.arquivado)
    .reduce((s,c)=>s+(parseFloat(c.valor)||0),0);
  const visitasHoje = cards.filter(c=>c.visita_data&&
    new Date(c.visita_data).toDateString()===new Date().toDateString());

  return (
    <div style={s.page}>
      <div style={s.tbar}>
        <div>
          <div style={s.tit}>CRM — Grupo D1 Carreiras</div>
          <div style={s.sub}>
            {cards.filter(c=>!c.arquivado).length} leads ativos
            {totalPipeline>0 && ` · Pipeline ${fmtBRL(totalPipeline)}`}
            {visitasHoje.length>0 && ` · 📅 ${visitasHoje.length} visita${visitasHoje.length>1?'s':''} hoje`}
          </div>
        </div>
        <div style={s.filters}>
          <button style={s.viewBtn(view==='kanban')} onClick={()=>setView('kanban')}>Kanban</button>
          <button style={s.viewBtn(view==='agenda')} onClick={()=>setView('agenda')}>
            Agendados{agendamentos.length ? ` (${agendamentos.length})` : ''}
          </button>
          <button style={s.btnSm} onClick={organizarAgenda} disabled={organizando}>
            {organizando ? 'Organizando...' : 'Organizar agenda'}
          </button>
          <input style={s.inp2} placeholder="🔍 Buscar lead..."
            value={filtros.busca} onChange={e=>setFiltros(f=>({...f,busca:e.target.value}))} />
          <select style={s.sel2} value={filtros.atendente_id}
            onChange={e=>setFiltros(f=>({...f,atendente_id:e.target.value}))}>
            <option value="">Todos atendentes</option>
            {atendentes.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select style={s.sel2} value={filtros.substatus}
            onChange={e=>setFiltros(f=>({...f,substatus:e.target.value}))}>
            <option value="">Todos status</option>
            {Object.entries(SUBSTATUS_CONFIG).map(([k,v])=>(
              <option key={k} value={k}>{v.icone} {v.label}</option>
            ))}
          </select>
        </div>
      </div>

      {view === 'agenda' ? (
        <AgendaView agendamentos={agendamentos} onOpenCard={abrirCardAgenda} />
      ) : (
        <div style={s.board}>
          {colunas.map(col=>(
            <Coluna key={col.id} col={col}
              cards={cardsDaCol(col.id)}
              atendentes={atendentes}
              onOpen={setAberto}
              onDragStart={()=>{}} onDragEnd={()=>{}}
              onDrop={onDrop}
              onNovo={setNovoCol} />
          ))}
        </div>
      )}

      {cardAberto && (
        <ModalDetalhes card={cardAberto} colunas={colunas} atendentes={atendentes}
          onClose={()=>setAberto(null)}
          onSalvo={()=>{carregar();}} />
      )}
      {novoCardCol && (
        <ModalNovo colunaId={novoCardCol} atendentes={atendentes}
          onClose={()=>setNovoCol(null)} onSalvo={carregar} />
      )}
    </div>
  );
}
